"use client";

import { Bell, BellRing, ChevronDown, LogOut, Menu, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TextField } from "@/components/ui/text-field";
import { cn } from "@/lib/cn";

import type { AdminLocale, AdminUser } from "@/store/admin-store";

type OpenMenu = "notifications" | "profile" | null;

export interface TopbarLabels {
  searchPlaceholder: string;
  notifications: string;
  openNotifications: string;
  noNotifications: string;
  noNotificationsDescription: string;
  openUserMenu: string;
  account: string;
  signOut: string;
  fallbackUserName: string;
  openNavigation: string;
}

interface AdminTopbarProps {
  user: AdminUser | null;
  locale: AdminLocale;
  searchQuery: string;
  mobileNavigationOpen: boolean;
  mobileMenuButtonRef: React.RefObject<HTMLButtonElement | null>;
  labels: TopbarLabels;
  onLocaleChange: (locale: AdminLocale) => void;
  onSearchQueryChange: (value: string) => void;
  onOpenMobileNavigation: () => void;
  onSignOut: () => void;
}

export function AdminTopbar({
  user,
  locale,
  searchQuery,
  mobileNavigationOpen,
  mobileMenuButtonRef,
  labels,
  onLocaleChange,
  onSearchQueryChange,
  onOpenMobileNavigation,
  onSignOut,
}: AdminTopbarProps) {
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const menuRootRef = useRef<HTMLDivElement | null>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement | null>(null);
  const profileButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!openMenu) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRootRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      const trigger = openMenu === "notifications" ? notificationsButtonRef.current : profileButtonRef.current;
      setOpenMenu(null);
      window.requestAnimationFrame(() => {
        trigger?.focus();
      });
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const userName = user?.name ?? labels.fallbackUserName;
  const userRole = user?.roleDisplayName ?? user?.role ?? "";
  const userInitial = (userName.trim()[0] ?? "A").toUpperCase();

  return (
    <header className="sticky top-3 z-30 mx-3 rounded-2xl border border-(--border) bg-surface shadow-(--shadow) lg:ml-0">
      <div className="flex min-h-22.5 flex-col gap-3 px-4 py-3 sm:px-5 lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-8 lg:py-0">
        <div className="flex w-full min-w-0 items-center gap-3 lg:max-w-72 xl:max-w-[28.25rem]">
          <button
            ref={mobileMenuButtonRef}
            type="button"
            onClick={onOpenMobileNavigation}
            aria-label={labels.openNavigation}
            aria-controls="admin-mobile-sidebar"
            aria-expanded={mobileNavigationOpen}
            className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-(--border) bg-surface text-dark/65 transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-4 h-4.5 w-4.5 -translate-y-1/2 text-dark/45" />
            <TextField
              value={searchQuery}
              onChange={(event) => {
                onSearchQueryChange(event.target.value);
              }}
              placeholder={labels.searchPlaceholder}
              aria-label={labels.searchPlaceholder}
              className="h-12 rounded-xl bg-surface pr-4 pl-11 shadow-none"
            />
          </div>
        </div>

        <div
          ref={menuRootRef}
          className="relative flex w-full min-w-0 items-center justify-between gap-2 sm:justify-end lg:w-auto lg:gap-3"
        >
          <div className="flex h-12 shrink-0 items-center rounded-xl border border-(--border) bg-surface p-1">
            <button
              type="button"
              onClick={() => {
                onLocaleChange("es");
              }}
              aria-pressed={locale === "es"}
              className={cn(
                "h-8 rounded-lg px-2.5 text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
                locale === "es" ? "bg-primary-soft text-dark" : "text-(--muted) hover:bg-dark/5 hover:text-dark",
              )}
            >
              ES
            </button>
            <span aria-hidden="true" className="h-5 w-px bg-(--border)" />
            <button
              type="button"
              onClick={() => {
                onLocaleChange("en");
              }}
              aria-pressed={locale === "en"}
              className={cn(
                "h-8 rounded-lg px-2.5 text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
                locale === "en" ? "bg-primary-soft text-dark" : "text-(--muted) hover:bg-dark/5 hover:text-dark",
              )}
            >
              EN
            </button>
          </div>

          <button
            ref={notificationsButtonRef}
            type="button"
            onClick={() => {
              setOpenMenu((current) => (current === "notifications" ? null : "notifications"));
            }}
            aria-label={labels.openNotifications}
            aria-haspopup="dialog"
            aria-controls={openMenu === "notifications" ? "admin-notifications-panel" : undefined}
            aria-expanded={openMenu === "notifications"}
            className={cn(
              "grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-(--border) bg-surface text-dark/65 transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
              openMenu === "notifications" && "border-primary-border bg-primary-soft text-primary",
            )}
          >
            <Bell className="h-5 w-5" />
          </button>

          <button
            ref={profileButtonRef}
            type="button"
            onClick={() => {
              setOpenMenu((current) => (current === "profile" ? null : "profile"));
            }}
            aria-label={labels.openUserMenu}
            aria-haspopup="menu"
            aria-controls={openMenu === "profile" ? "admin-profile-menu" : undefined}
            aria-expanded={openMenu === "profile"}
            className={cn(
              "flex h-12 min-w-0 items-center gap-2 rounded-xl border border-(--border) bg-surface px-2 transition-colors hover:bg-primary-soft focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:min-w-44 sm:px-2.5",
              openMenu === "profile" && "border-primary-border bg-primary-soft",
            )}
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-action text-sm font-semibold text-on-primary">
              {userInitial}
            </span>
            <span className="hidden min-w-0 flex-1 text-left sm:block">
              <span className="block truncate text-xs font-semibold text-dark">{userName}</span>
              <span className="block truncate text-[0.6875rem] text-(--muted)">{userRole}</span>
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-dark/55 transition-transform duration-150",
                openMenu === "profile" && "rotate-180",
              )}
            />
          </button>

          {openMenu === "notifications" ? (
            <section
              id="admin-notifications-panel"
              role="dialog"
              aria-labelledby="admin-notifications-title"
              className="absolute top-[calc(100%+0.75rem)] right-0 z-50 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-(--border) bg-surface shadow-(--shadow)"
            >
              <div className="border-b border-(--border) px-5 py-4">
                <h2 id="admin-notifications-title" className="text-sm font-semibold text-dark">
                  {labels.notifications}
                </h2>
              </div>
              <div className="flex flex-col items-center px-6 py-8 text-center">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-primary-soft text-primary">
                  <BellRing className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-semibold text-dark">{labels.noNotifications}</p>
                <p className="mt-1 text-xs leading-5 text-(--muted)">{labels.noNotificationsDescription}</p>
              </div>
            </section>
          ) : null}

          {openMenu === "profile" ? (
            <div
              id="admin-profile-menu"
              role="menu"
              aria-label={labels.account}
              className="absolute top-[calc(100%+0.75rem)] right-0 z-50 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-(--border) bg-surface shadow-(--shadow)"
            >
              <div className="px-5 py-4">
                <p className="truncate text-sm font-semibold text-dark">{userName}</p>
                <p className="mt-0.5 truncate text-xs text-(--muted)">{user?.email ?? ""}</p>
                {userRole ? <p className="mt-2 text-xs font-medium text-primary">{userRole}</p> : null}
              </div>
              <div className="border-t border-(--border) p-2">
                <button
                  type="button"
                  role="menuitem"
                  onClick={onSignOut}
                  className="flex h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold text-dark transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
                >
                  <LogOut className="h-4.5 w-4.5" />
                  <span>{labels.signOut}</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
